import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItemRadio,
  Toolbar,
  ToolbarButton,
  ToolbarGroup,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Field,
} from "@fluentui/react-components";
import {
  AddRegular,
  ArrowClockwise24Regular,
  BookmarkRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
  FilterRegular,
  FilterDismissRegular,
  FilterAddRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

// Business logic
import { useSelectedCisContext } from "../context/SelectedCisContext";
import { AuthContext } from "../../../context/AuthContext";
import { useCreateFilter } from "../../filters/hooks/useFilterMutations";
import { readFilters } from "../../filters/services/filterApi";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";

// Variables
//const apiUrl = process.env.REACT_APP_API_URL?.trim();

const CisToolbar = ({ setIsRemoverOpen, setIsFiltersOpen, queryParameters }) => {
  const { t } = useTranslation();
  const { value: SelectedCis, setValue: SetSelectedCis } = useSelectedCisContext();
  const { user } = useContext(AuthContext) || {};

  // Navigation and State
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const [isSaveFilterDialogOpen, setIsSaveFilterDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [activeFilterId, setActiveFilterId] = useState(null);

  // Data
  const queryClient = useQueryClient();
  const { data: filtersData = [] } = useItemQuery(["filters", "cis"], () => readFilters({ module: "cis" }), true);

  // Mutations
  const { mutate: createFilterMutate } = useCreateFilter(
    "filters",
    (data) => {
      toast.success(
        t("filter.messages.saved", { name: data.name, defaultValue: `Filter "${data.name}" saved successfully` }),
      );
      setIsSaveFilterDialogOpen(false);
      setFilterName("");
      queryClient.invalidateQueries({ queryKey: ["filters"] });
    },
    (error) => {
      toast.error(error.message || t("filter.messages.saveError", { defaultValue: "Failed to save filter" }));
    },
    false,
  );

  // Effect to detect when filters are modified (don't match any saved view)
  useEffect(() => {
    if (!filtersData || filtersData.length === 0) {
      setActiveFilterId(null);
      return;
    }

    // Check if current query parameters match any saved filter
    const matchingFilter = filtersData.find((filter) => {
      const filterParams = filter.filter || {};
      const currentParams = queryParameters || {};

      // Compare keys and values
      const filterKeys = Object.keys(filterParams).filter(
        (key) => filterParams[key] !== null && filterParams[key] !== undefined && filterParams[key] !== "",
      );
      const currentKeys = Object.keys(currentParams).filter(
        (key) => currentParams[key] !== null && currentParams[key] !== undefined && currentParams[key] !== "",
      );

      if (filterKeys.length !== currentKeys.length) return false;

      return filterKeys.every((key) => String(currentParams[key]) === String(filterParams[key]));
    });

    setActiveFilterId(matchingFilter?.id || null);
  }, [queryParameters, filtersData]);

  // Handler Effects
  const handleClickNew = () => navigate(`/ci/new`);
  const handleClickEdit = () => navigate(`/ci/${SelectedCis?.[0]?.public_id}`);
  const handleClickReset = () => navigate({ search: "" }, { replace: true });
  const handleClickFilters = () => setIsFiltersOpen((prev) => !prev);

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error(t("filter.messages.nameRequired", { defaultValue: "Filter name is required" }));
      return;
    }

    const filterData = {
      module: "cis",
      user_id: user?.id || null,
      name: filterName.trim(),
      filter: queryParameters || {},
    };

    createFilterMutate(filterData);
  };

  const handleApplyFilter = (filter) => {
    const filterParams = filter.filter || {};
    const params = new URLSearchParams();
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.append(key, value);
      }
    });
    setActiveFilterId(filter.id);
    navigate({ search: params.toString() }, { replace: true });
  };

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <ToolbarGroup>
        <ToolbarButton icon={<AddRegular />} aria-label="Add" onClick={handleClickNew}>
          {t("generic.add")}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(SelectedCis?.length === 1)}
          icon={<EditRegular />}
          aria-label="Edit"
          onClick={handleClickEdit}
        >
          {t("generic.edit")}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(SelectedCis?.length === 1)}
          icon={<DeleteRegular />}
          aria-label="Delete"
          onClick={() => setIsRemoverOpen(true)}
        >
          {t("generic.delete")}
        </ToolbarButton>
        <ToolbarButton
          icon={<ArrowClockwise24Regular />}
          onClick={() => queryClient?.invalidateQueries({ queryKey: ["cis"] })}
        >
          {t("generic.refresh")}
        </ToolbarButton>
        <ToolbarButton
          disabled={!(SelectedCis?.length > 0)}
          icon={<DismissRegular />}
          onClick={() => SetSelectedCis([])}
        >
          {t("generic.unselect")}
        </ToolbarButton>
      </ToolbarGroup>
      <ToolbarGroup align="end">
        <ToolbarButton icon={<FilterRegular />} onClick={handleClickFilters}>
          {t("generic.filters")}
        </ToolbarButton>
        <ToolbarButton icon={<FilterAddRegular />} onClick={() => setIsSaveFilterDialogOpen(true)}>
          {t("generic.save")}
        </ToolbarButton>
        <Menu id="menuFilters">
          <MenuTrigger disableButtonEnhancement>
            <ToolbarButton icon={<BookmarkRegular />}>{t("generic.views")}</ToolbarButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {filtersData?.length > 0 && (
                <>
                  {filtersData.map((filter) => (
                    <MenuItemRadio
                      key={filter.id}
                      name="viewSelection"
                      value={filter.id}
                      checked={activeFilterId === filter.id}
                      onClick={() => handleApplyFilter(filter)}
                    >
                      {filter.name}
                    </MenuItemRadio>
                  ))}
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
        <Dialog open={isSaveFilterDialogOpen} onOpenChange={(_, data) => setIsSaveFilterDialogOpen(data.open)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{t("nounPhrases.saveFilter", { defaultValue: "Save Filter" })}</DialogTitle>
              <DialogContent>
                <Field label={t("generic.name", { defaultValue: "Name" })} required>
                  <Input
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder={t("nounPhrases.enterItemName", { defaultValue: "Enter filter name" })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveFilter();
                      }
                    }}
                  />
                </Field>
                <div style={{ marginTop: "1rem" }}>
                  <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>Current Filter:</label>
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: "12px",
                      borderRadius: "4px",
                      overflow: "auto",
                      maxHeight: "200px",
                      fontSize: "12px",
                      margin: 0,
                    }}
                  >
                    {JSON.stringify(queryParameters || {}, null, 2)}
                  </pre>
                </div>
              </DialogContent>
            </DialogBody>
            <DialogActions style={{ marginTop: "1.5rem" }}>
              <Button appearance="secondary" onClick={() => setIsSaveFilterDialogOpen(false)}>
                {t("generic.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button appearance="primary" onClick={handleSaveFilter}>
                {t("generic.save", { defaultValue: "Save" })}
              </Button>
            </DialogActions>
          </DialogSurface>
        </Dialog>
        <ToolbarButton
          icon={<FilterDismissRegular />}
          onClick={handleClickReset}
          disabled={search === "" || search === undefined}
        />
      </ToolbarGroup>
    </Toolbar>
  );
};

export default CisToolbar;
